import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  getGeocode,
  getLatLng
} from "use-places-autocomplete";
import { ProfileAddressType } from "@motorata/base-ui.types.api/Profile";
import { Box, Row } from "@motorata/base-ui.components.atoms._atoms";
import { Label } from "@motorata/base-ui.components.atoms.typography";
import { IconButton } from "@motorata/base-ui.components.atoms.button";
import Spinner from "@motorata/base-ui.components.atoms.spinner";
import Link from "next/link";

const MapBlock = ({ address, zoom = 15, height = "200px", type = "car" }: {
  address: ProfileAddressType
  zoom?: number
  height?: number | string
  type?: "car" | "company"
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY
  });

  const [stateZoom, setStateZoom] = useState(zoom);
  const [coordinates, setCoordinates] = useState<{ lat: number, lng: number } | null>(null);

  const fetchCoordinates = useCallback(async () => {
    const res = await getGeocode({ address: `${address.address}, ${address.city}` });
    const { lat, lng } = getLatLng(res[0]);
    setCoordinates({
      lat,
      lng
    });
  }, []);

  useEffect(() => {
    if (isLoaded) {
      fetchCoordinates();
    }
  }, [isLoaded]);

  return <Box height={height} width="100%">
    {isLoaded && coordinates ? (
        <>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%"
            }}
            center={{
              lat: coordinates.lat,
              lng: coordinates.lng
            }}
            zoom={stateZoom}
            options={{
              disableDefaultUI: true,
              mapTypeControl: false,
              zoomControl: false,
              fullscreenControl: false,
              streetViewControl: false
            }}
          >
            <Marker
              icon={{
                url: `https://pl-motorata-s3-public.s3.eu-central-1.amazonaws.com/${type === "car" ? "map_car" : "map_company"}.svg`,
                scaledSize: new google.maps.Size(37, 37)
              }}
              position={{
                lat: coordinates.lat,
                lng: coordinates.lng
              }} />
          </GoogleMap>
          <AddressNavigation>
            <IconButton
              size="XL"
              icon="plus"
              onClick={() => setStateZoom(stateZoom < 17 ? stateZoom + 1 : stateZoom)}
              background="white"
              marginBottom="XXS"
              hoverColor="primary"
              style={{
                borderRadius: 3
              }}
            />
            <IconButton
              size="XL"
              icon="minus"
              onClick={() => setStateZoom(stateZoom - 1)}
              background="white"
              hoverColor="primary"
              style={{
                borderRadius: 3
              }}
            />
          </AddressNavigation>
          <AddressWrapper>
            <Box>
              <Label type="secondary" icon="location-fill">
                {address.address}
              </Label>
              <Label type="third" paddingLeft="24px">
                {address.city}, {address.postCode}
              </Label>
            </Box>
            <Link href={`https://maps.google.com/?q=${coordinates.lat},${coordinates.lng}`} target="_blank">
              <IconButton
                size="XL"
                icon="map"
                onClick={() => {}}
                background="none"
                hoverColor="primary"
                style={{
                  borderRadius: 3
                }}
              />
            </Link>
          </AddressWrapper>
        </>
      ) :
      <Row width="100%" height="100%" alignItems="center" justifyContent="center">
        <Spinner />
      </Row>
    }
  </Box>;
};

export default MapBlock;

const AddressNavigation = styled(Box)`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: ${({ theme }) => theme.space.XS};
  right: ${({ theme }) => theme.space.XS};
`;

const AddressWrapper = styled(Box)`
  position: absolute;
  bottom: ${({ theme }) => theme.space.XS};
  left: ${({ theme }) => theme.space.XS};
  width: calc(100% - (${({ theme }) => theme.space.XS} * 2));
  background: white;
  padding: ${({ theme }) => theme.space.XS};
  box-shadow: ${({ theme }) => theme.variable.boxShadow};
  border-radius: 3px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

